// src/quality/qc.ts
// Раздел «Контроль качества медицинских карт» — общий слой данных для обоих дашбордов.
// Единица счёта — КАРТА. scope: 'hospital' (живьём) | 'app' (снапшот).

export type QcScope = "app" | "hospital";

export interface QcFilters {
  mis_id?: string | null;
  place?: string | null; // КС | ДС (только стационар)
  year?: number | null;
  month?: number | null;
  day?: number | null;
  date_from?: string | null;
  date_to?: string | null;
  bed_profiles?: string | null; // имена профилей коек через '||' (охрана материнства)
}

export interface QcSummary {
  kpi: {
    checked_cards: number;
    defective_cards: number;
    fixed_cards: number;
    fixed_pct: number;
    open_cards: number;
    open_pct: number;
    critical_cards: number;
    saved_cost?: number; // АПП: сэкономлено, ₸ (стоимость исправленных дефектов)
  };
  donut: { key: string; label: string; value: number; pct: number }[];
}

export interface QcBreakdown {
  types: { label: string; cards: number }[];
  departments: { label: string; checked: number; defective: number; clean_pct: number }[];
}

export interface QcDynamics {
  bucket_kind: "day" | "week" | "month";
  points: { bucket: string; defective_cards: number }[];
}

// Палитра дашборда
export const QC_COLORS = {
  page: "#083264",
  card: "#0e2b4b",
  border: "rgba(255,255,255,0.24)",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.82)",
  fixed: "#48B1CC", // исправлено
  open: "#DC143C", // не исправлено
  critical: "#eee36d", // критично
  bar: "#083264",
  barAccent: "#48B1CC",
};

interface AuthPayload {
  access_token?: string;
  iin?: string;
}

function getStoredAuth(): AuthPayload | null {
  try {
    const raw = localStorage.getItem("auth_payload");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthPayload;
    if (!parsed?.access_token || !parsed?.iin) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Тот же resolve, что у рабочих hospital-запросов (hospital/api.ts getApiBase),
// чтобы бить в тот же бэкенд дашбордов. Для АПП — фиксированный прокси /dashboards-api.
function apiBaseFor(scope: QcScope): string {
  if (scope === "app") return "/dashboards-api";
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const dashboardsBase = String(env?.VITE_DASHBOARDS_API_BASE || "").replace(/\/+$/, "");
  if (dashboardsBase) return dashboardsBase;
  return String(env?.VITE_API_BASE || "").replace(/\/+$/, "");
}

function buildUrl(scope: QcScope, path: string, query: Record<string, string | number | undefined | null>): string {
  const base = apiBaseFor(scope);
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (/\/api$/i.test(base) && /^\/api\//i.test(normalizedPath)) {
    normalizedPath = normalizedPath.replace(/^\/api/i, "");
  }
  const full = new URL(`${base}${normalizedPath}`, window.location.origin);
  Object.entries(query).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") return;
    full.searchParams.set(k, String(v));
  });
  return full.toString();
}

// Короткий кэш: KPI+donut делят summary, типы+отделения делят breakdown.
const _cache = new Map<string, { ts: number; promise: Promise<unknown> }>();
const _CACHE_TTL_MS = 15000;

async function qcGet<T>(scope: QcScope, endpoint: string, filters: QcFilters): Promise<T> {
  const cacheKey = JSON.stringify([scope, endpoint, filters]);
  const hit = _cache.get(cacheKey);
  const now = Date.now();
  if (hit && now - hit.ts < _CACHE_TTL_MS) {
    return hit.promise as Promise<T>;
  }
  const promise = _qcGetRaw<T>(scope, endpoint, filters);
  _cache.set(cacheKey, { ts: now, promise });
  promise.catch(() => _cache.delete(cacheKey)); // не кэшировать ошибку
  return promise;
}

async function _qcGetRaw<T>(scope: QcScope, endpoint: string, filters: QcFilters): Promise<T> {
  const auth = getStoredAuth();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (auth?.access_token) headers.Authorization = `Bearer ${auth.access_token}`;
  if (auth?.iin) headers["X-User-IIN"] = auth.iin;

  const url = buildUrl(scope, `/api/v1/quality/${endpoint}`, {
    scope,
    mis_id: filters.mis_id ?? undefined,
    place: filters.place ?? undefined,
    year: filters.year ?? undefined,
    month: filters.month ?? undefined,
    day: filters.day ?? undefined,
    date_from: filters.date_from ?? undefined,
    date_to: filters.date_to ?? undefined,
    bed_profiles: filters.bed_profiles ?? undefined,
  });

  const res = await fetch(url, { method: "GET", credentials: "omit", headers });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data && typeof data === "object" && "detail" in data) {
        message = String((data as { detail?: unknown }).detail ?? message);
      }
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const fetchQcSummary = (scope: QcScope, f: QcFilters) => qcGet<QcSummary>(scope, "summary", f);
export const fetchQcBreakdown = (scope: QcScope, f: QcFilters) => qcGet<QcBreakdown>(scope, "breakdown", f);
export const fetchQcDynamics = (scope: QcScope, f: QcFilters) => qcGet<QcDynamics>(scope, "dynamics", f);

export function monthShort(iso: string): string {
  // '2026-04-01T..' -> '04.2026' ; неделя -> 'дд.мм'
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

export function weekShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}


// ── Drill-down: строка карты + открытие таблицы в новой вкладке ───────────
export interface QcCardRow {
  doc_id: string;
  mis_id: string;
  history_number: string;
  date: string | null;
  department: string;
  doctor: string;
  mkb: string;
  types: string[];
  defects: number;
  status: string; // clean | fixed | open | critical
  hours_open: number | null;
}

export function buildQualityCardsHref(
  scope: QcScope,
  filters: QcFilters,
  drill: string,
  extra: Record<string, string | number | undefined | null> = {},
  title?: string,
): string {
  const qs = new URLSearchParams();
  qs.set("scope", scope);
  if (filters.mis_id) qs.set("mis_id", String(filters.mis_id));
  if (filters.place) qs.set("place", String(filters.place));
  if (filters.year != null) qs.set("year", String(filters.year));
  if (filters.month != null) qs.set("month", String(filters.month));
  if (filters.day != null) qs.set("day", String(filters.day));
  if (filters.date_from) qs.set("date_from", String(filters.date_from));
  if (filters.date_to) qs.set("date_to", String(filters.date_to));
  if (filters.bed_profiles) qs.set("bed_profiles", String(filters.bed_profiles));
  qs.set("drill", drill);
  Object.entries(extra).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") return;
    qs.set(k, String(v));
  });
  if (title) qs.set("title", title);
  return `/dashboards/app/quality-cards?${qs.toString()}`;
}

export function openQualityCardsTab(
  scope: QcScope,
  filters: QcFilters,
  drill: string,
  extra: Record<string, string | number | undefined | null> = {},
  title?: string,
): void {
  const href = buildQualityCardsHref(scope, filters, drill, extra, title);
  window.open(href, "_blank", "noopener,noreferrer");
}

export const QC_STATUS_LABEL: Record<string, string> = {
  clean: "Без дефектов",
  fixed: "Исправлено",
  open: "Не исправлено",
  critical: "Критично",
};
